import React, { useCallback, useState } from "react"
import { TextField } from "@mui/material"
import { z as zod } from "zod"
import type { FactorRuleField } from "../types"

export interface FactorRuleInputProps {
  ruleId: string | undefined
  field: FactorRuleField
  value: string | undefined
  onBlur: (value: string | undefined) => void
  onChange?: (value: string | undefined) => void
}

// Only allow numbers (positive or negative), decimals, and empty string
const numericInputSchema = zod.union([
  zod.string().length(0),
  zod.string().regex(/^-?\d*\.?\d*$/),
])

export default function FactorRuleInput({ ruleId, field, value, onBlur, onChange }: FactorRuleInputProps) {
  const [inputValue, setInputValue] = useState("")
  const [isFocused, setIsFocused] = useState(false)

  const displayValue = isFocused ? inputValue : (value ?? "")

  function handleChange(event: React.ChangeEvent<HTMLInputElement>) {
    if (numericInputSchema.safeParse(event.target.value).success) {
      setInputValue(event.target.value)
      if (onChange) {
        onChange(event.target.value ?? undefined)
      }
    }
  }

  const handleFocus = useCallback(() => {
    setIsFocused(true)
    setInputValue(value ?? "")
  }, [value])

  const handleBlur = useCallback(() => {
    setIsFocused(false)
    onBlur(inputValue)
  }, [onBlur, inputValue])

  return <TextField
    className="factor-rule-input"
    variant="outlined"
    size="small"
    id={`${ruleId}-${field}`}
    placeholder="Value"
    value={displayValue}
    onFocus={handleFocus}
    onBlur={handleBlur}
    onChange={handleChange}
  />
}
